
"use client";

import React, { createContext, useContext, useState, useEffect } from "react";

// Supported languages
export type Language = "ko" | "en" | "th" | "zh" | "ja" | "vi" | "uz";

// Theme types
export type Theme = "light" | "dark";

// Context type definition
interface SettingsContextType {
  theme: Theme;
  language: Language;
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
}

// Create context
const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

// Provider component
export function SettingsProvider({ children }: { children: React.ReactNode }) {
  // Initialize with default values
  const [theme, setThemeState] = useState<Theme>("light");
  const [language, setLanguageState] = useState<Language>("ko");
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const savedTheme = (localStorage.getItem("theme") as Theme) || "light";
    const savedLanguage = (localStorage.getItem("language") as Language) || "ko";
    
    // Batch state updates (intentional hydration pattern)
    /* eslint-disable */
    setThemeState(savedTheme);
    setLanguageState(savedLanguage);
    setIsHydrated(true);
    /* eslint-enable */
  }, []);

  // Apply theme class when hydrated or theme changes
  useEffect(() => {
    if (!isHydrated) return;
    
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme, isHydrated]);

  // Theme setter with persistence
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);

    // Toggle dark class on html element
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  // Language setter with persistence
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  return (
    <SettingsContext.Provider
      value={{ theme, language, setTheme, setLanguage }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

// Custom hook to use settings context
export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}

